/**
 * Build styles
 */
import Login from "../../../views/Login";

require('./index.css').toString();
import {v4 as uuidv4} from 'uuid';

/**
 * @class QuizMulti
 * @classdesc Quiz Multi for Editor.js
 * @property {QuizMultiData} data - Quiz Multi`s input and output data
 * @property {object} api - Editor.js API instance
 *
 * @typedef {object} QuizMultiData
 * @description Quiz Multi`s input and output data
 * @property {string} title - warning`s title
 * @property {string} message - warning`s message
 *
 * @typedef {object} QuizMultiConfig
 * @description Quiz Multi`s initial configuration
 * @property {string} titlePlaceholder - placeholder to show in warning`s title input
 * @property {string} messagePlaceholder - placeholder to show in warning`s message input
 */
export default class QuizMulti {

    /**
     * Notify core that read-only mode is supported
     */
    static get isReadOnlySupported() {
        return true;
    }

    /**
     * Get Toolbox settings
     *
     * @public
     * @returns {string}
     */
    static get toolbox() {
        return {
            icon: '<svg width="15" height="18" aria-hidden="true" focusable="false" data-prefix="far" data-icon="ballot-check" class="svg-inline--fa fa-ballot-check fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M344 360H200c-4.4 0-8 3.6-8 8v32c0 4.4 3.6 8 8 8h144c4.4 0 8-3.6 8-8v-32c0-4.4-3.6-8-8-8zm-232 56h32c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16zm0-256h32c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16zm88-8h144c4.4 0 8-3.6 8-8v-32c0-4.4-3.6-8-8-8H200c-4.4 0-8 3.6-8 8v32c0 4.4 3.6 8 8 8zM416 0H32C14.3 0 0 14.3 0 32v448c0 17.7 14.3 32 32 32h384c17.7 0 32-14.3 32-32V32c0-17.7-14.3-32-32-32zm-16 464H48V48h352v416zM134.6 286.4c2.1 2.1 5.5 2.1 7.6 0l64.2-63.6c2.1-2.1 2.1-5.5 0-7.6l-12.6-12.7c-2.1-2.1-5.5-2.1-7.6 0l-47.6 47.2-20.6-20.9c-2.1-2.1-5.5-2.1-7.6 0l-12.7 12.6c-2.1 2.1-2.1 5.5 0 7.6l36.9 37.4zM344 232H237.4c-1.9 5-4.6 9.7-8.5 13.5L194.2 280H344c4.4 0 8-3.6 8-8v-32c0-4.4-3.6-8-8-8z"></path></svg>',
            title: 'Quiz Multiple Choice'
        };
    }

    /**
     * Allow to press Enter inside the QuizMulti
     *
     * @public
     * @returns {boolean}
     */
    static get enableLineBreaks() {
        return true;
    }

    /**
     * Default placeholder for warning title
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_TITLE_PLACEHOLDER() {
        return 'Question';
    }

    /**
     * Default placeholder for warning message
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_MESSAGE_PLACEHOLDER() {
        return 'Description & Help';
    }

    /**
     * Quiz Multi`s styles
     *
     * @returns {object}
     */
    get CSS() {
        return {
            baseClass: this.api.styles.block,
            wrapper: 'ejsqm',
            title: 'cdx-warning__title',
            input: this.api.styles.input,
            message: 'cdx-warning__message',
        };
    }

    /**
     * Render plugin`s main Element and fill it with saved data
     *
     * @param {QuizMultiData} data — previously saved data
     * @param {QuizMultiConfig} config — user config for Tool
     * @param {object} api - Editor.js API
     * @param {boolean} readOnly - read-only mode flag
     */
    constructor({data, config, api, readOnly}) {
        this.api = api;
        this.readOnly = readOnly;

        this.titlePlaceholder = config.titlePlaceholder || QuizMulti.DEFAULT_TITLE_PLACEHOLDER;
        this.messagePlaceholder = config.messagePlaceholder || QuizMulti.DEFAULT_MESSAGE_PLACEHOLDER;

        this.data = {
            title: data.title || '',
            message: data.message || '',
            id: data.id || uuidv4(),
            answers: (data.answers) ? JSON.parse(data.answers) : []
        };

        this.answerEl = null;
    }

    /**
     * Create Quiz Multi container with inputs
     *
     * @returns {Element}
     */
    render() {
        const container = this._make('div', [this.CSS.baseClass, this.CSS.wrapper]);
        const title = this._make('div', [this.CSS.input, this.CSS.title], {
            contentEditable: !this.readOnly,
            innerHTML: this.data.title,
        });
        const message = this._make('div', [this.CSS.input, this.CSS.message], {
            contentEditable: !this.readOnly,
            innerHTML: this.data.message,
        });

        this.answerEl = this._make('div', ['ejsqm-answers'], {});
        this.answerEl.appendChild(this._generateAnswers());

        const add = this._make('div', ['ejsqm-add-answer'], {});

        const answerInput = this._make('div', [this.CSS.input], {
            contentEditable: true,
        });

        const correctDiv = this._make('div', ['ejsqm-correct-input'], {});
        const correctLabel = this._make('label', null, {
            innerHTML: ' &nbsp;Correct Answer'
        });
        const correctInput = this._make('input', null, {
            type: 'checkbox',
        });

        correctLabel.prepend(correctInput);
        correctDiv.appendChild(correctLabel);

        const button = this._make('button', ['is-small', 'is-success', 'button'], {
            innerHTML: 'Add'
        });

        button.onclick = () => {
            this.data.answers.push({answer: answerInput.innerHTML, correct: correctInput.checked});
            answerInput.innerHTML = '';
            correctInput.checked = false;
            this.answerEl.innerHTML = '';
            this.answerEl.appendChild(this._generateAnswers());
        };

        answerInput.dataset.placeholder = 'Answer Choice...';

        add.appendChild(answerInput);
        add.appendChild(correctDiv);
        add.appendChild(button);

        title.dataset.placeholder = this.titlePlaceholder;
        message.dataset.placeholder = this.messagePlaceholder;

        container.appendChild(title);
        container.appendChild(message);
        container.appendChild(this.answerEl);
        container.appendChild(add);

        return container;
    }

    _generateAnswers() {
        const answerList = this._make('div', ['ejsqm-answer-list'], {});

        for (let i = 0; i < this.data.answers.length; i++) {
            const answer = this.data.answers[i];
            const addAnswer = this._make('div', ['ejsqm-answer'], {
                innerHTML: '<div class="ejsqm-answer-label">' + ((answer.correct) ? '<i class="far fa-check"></i>' : '<i class="far fa-circle"></i>') + answer.answer + '</div>'
            });

            const removeButton = this._make('button', ['button', 'is-small', 'is-light'], {
                innerHTML: '<i class="fas fa-times"></i>'
            });

            removeButton.onclick = () => {
                console.log('Remove!');
                this.data.answers.splice(i, 1);
                this.answerEl.innerHTML = '';
                this.answerEl.appendChild(this._generateAnswers());
            };

            addAnswer.appendChild(removeButton);
            addAnswer.appendChild(this._make('div', null, {
                style: 'clear: both'
            }));

            answerList.appendChild(addAnswer);
        }

        return answerList;
    }

    /**
     * Extract QuizMulti data from Quiz Multi element
     *
     * @param {HTMLDivElement} warningElement - element to save
     * @returns {QuizMultiData}
     */
    save(warningElement) {
        const title = warningElement.querySelector(`.${this.CSS.title}`);
        const message = warningElement.querySelector(`.${this.CSS.message}`);


        return Object.assign(this.data, {
            title: title.innerHTML,
            message: message.innerHTML,
            answers: JSON.stringify(this.data.answers)
        });
    }

    /**
     * Helper for making Elements with attributes
     *
     * @param  {string} tagName           - new Element tag name
     * @param  {Array|string} classNames  - list or name of CSS classname(s)
     * @param  {object} attributes        - any attributes
     * @returns {Element}
     */
    _make(tagName, classNames = null, attributes = {}) {
        const el = document.createElement(tagName);

        if (Array.isArray(classNames)) {
            el.classList.add(...classNames);
        } else if (classNames) {
            el.classList.add(classNames);
        }

        for (const attrName in attributes) {
            el[attrName] = attributes[attrName];
        }

        return el;
    }

    /**
     * Sanitizer config for Quiz Multi saved data
     *
     * @returns {object}
     */
    static get sanitize() {
        return {
            title: {},
            message: {},
        };
    }
}
